import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { generateCareerMap } from '../services/careerService';
import { customToast } from '../utils/toastUtils';
import './AIImprovementPage.css';
import './AICareerMap.css';

const AICareerMap = () => {
  const { userData } = useUser();
  const navigate = useNavigate();

  const STORAGE_KEY = 'ai_career_map_state';

  const [currentJobTitle, setCurrentJobTitle] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.currentJobTitle || ''; } catch { return ''; }
  });
  const [dreamJobTitle, setDreamJobTitle] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.dreamJobTitle || ''; } catch { return ''; }
  });
  const [currentSkills, setCurrentSkills] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.currentSkills || ''; } catch { return ''; }
  });
  const [experienceRange, setExperienceRange] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.experienceRange || ''; } catch { return ''; }
  });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.results || null; } catch { return null; }
  });

  // Persist state to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      currentJobTitle, dreamJobTitle, currentSkills, experienceRange, results
    }));
  }, [currentJobTitle, dreamJobTitle, currentSkills, experienceRange, results]);

  const handleGenerateMap = async () => {
    // Validate all required fields
    if (!currentJobTitle || !dreamJobTitle || !currentSkills || !experienceRange) {
      customToast.error('All fields are required');
      return;
    }

    if (!userData?.uid) {
      customToast.error('Please login to use this feature');
      return;
    }

    setLoading(true);
    
    try {
      const response = await generateCareerMap({
        userId: userData.uid,
        currentJobTitle,
        dreamJobTitle,
        currentSkills,
        experienceRange
      });

      if (response.success) {
        setResults(response.data);
        customToast.success('Career map generated successfully!');
        
        // Scroll to results section
        setTimeout(() => {
          document.getElementById('career-map-results')?.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }, 500);
      } else {
        customToast.error(response.error || 'Failed to generate career map');
      }
    } catch (error) {
      console.error('❌ Career map error:', error);
      customToast.error(error.message || 'Failed to generate career map');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tool-page">
      {/* Hero Section */}
      <div className="career-map-hero">
        <div className="career-map-hero-content">
          <div className="hero-badge">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>AI Career Map</span>
          </div>
          <h1 className="career-map-title">
            Get a Clear, Structured Roadmap from Your Current Position to Your Dream Role
          </h1>
          <p className="career-map-tagline">
            AI-guided career progression, skill planning, and actionable growth steps
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="career-map-content">
        {/* Description Card */}
        <div className="career-map-description-card">
          <div className="description-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="url(#descGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <defs>
                <linearGradient id="descGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#10b981"/>
                  <stop offset="100%" stopColor="#059669"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          <p className="career-map-description">
            The AI Career Map provides a personalized career progression plan that shows how to move from your current 
            role to your desired job title. It outlines the intermediate roles, required skills, and preparation strategy needed 
            at each stage. This feature removes uncertainty by giving you a clear direction and actionable steps, helping 
            you understand what to learn, when to learn it, and how to position yourself for long-term career success and 
            advancement.
          </p>
        </div>

        {/* Input Form Section */}
        <div className="career-map-form-section">
          <h2 className="section-title">Generate Your Career Roadmap</h2>
          <div className="career-map-form">
            <div className="form-group">
              <label className="form-label">
                Current Job Title <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Junior Software Engineer, Marketing Analyst"
                value={currentJobTitle}
                onChange={(e) => setCurrentJobTitle(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Dream Job Title <span className="required">*</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., Engineering Manager, Marketing Director"
                value={dreamJobTitle}
                onChange={(e) => setDreamJobTitle(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Current Skills <span className="required">*</span>
              </label>
              <textarea
                className="form-textarea"
                placeholder="e.g., React, Python, Project Management, Data Analysis..."
                rows="4"
                value={currentSkills}
                onChange={(e) => setCurrentSkills(e.target.value)}
              />
              <span className="form-hint">List all your current technical and soft skills</span>
            </div>

            <div className="form-group">
              <label className="form-label">
                Experience Range <span className="required">*</span>
              </label>
              <select 
                className="form-select"
                value={experienceRange}
                onChange={(e) => setExperienceRange(e.target.value)}
              >
                <option value="">Select your experience range</option>
                <option value="0-1 years">0-1 years</option>
                <option value="1-3 years">1-3 years</option>
                <option value="3-5 years">3-5 years</option>
                <option value="5-8 years">5-8 years</option>
                <option value="8-10 years">8-10 years</option>
                <option value="10+ years">10+ years</option>
              </select>
            </div>

            <button 
              className="career-map-cta-button"
              onClick={handleGenerateMap}
              disabled={!currentJobTitle || !dreamJobTitle || !currentSkills || !experienceRange || loading}
            >
              {loading ? (
                <>
                  <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/>
                    <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 013.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Generate Career Map
                </>
              )}
            </button>
          </div>
        </div>

        {/* Show How It Works and What Roadmap Includes only when no results */}
        {!results && (
          <>
            {/* How It Works Section */}
            <div className="how-it-works-section">
              <h2 className="section-title">How It Works</h2>
              <div className="steps-grid">
                <div className="step-card">
                  <div className="step-number">1</div>
                  <h3>Enter Your Current Job Title</h3>
                  <p>Specify your current role or job position</p>
                </div>

                <div className="step-card">
                  <div className="step-number">2</div>
                  <h3>Enter Your Dream Job Title</h3>
                  <p>Define the role you aspire to achieve</p>
                </div>

                <div className="step-card">
                  <div className="step-number">3</div>
                  <h3>Add Your Current Skills</h3>
                  <p>List all your technical and soft skills</p>
                </div>

                <div className="step-card">
                  <div className="step-number">4</div>
                  <h3>Enter Your Experience Range</h3>
                  <p>Select your years of professional experience</p>
                </div>

                <div className="step-card">
                  <div className="step-number">5</div>
                  <h3>Click on Generate Career Map Button</h3>
                  <p>Submit to initiate AI roadmap generation</p>
                </div>

                <div className="step-card">
                  <div className="step-number">6</div>
                  <h3>AI Builds a Personalized Career Roadmap</h3>
                  <p>Receive detailed progression path and strategy</p>
                </div>
              </div>
            </div>

            {/* What the Roadmap Includes Section */}
            <div className="roadmap-includes-section">
              <h2 className="section-title">What the Roadmap Includes</h2>
              <div className="includes-grid">
                <div className="include-card">
                  <div className="include-icon green">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Career Progression Path</h3>
                  <p>See intermediate roles between your current and dream role.</p>
                </div>

                <div className="include-card">
                  <div className="include-icon blue">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Skill Requirements Breakdown</h3>
                  <p>Understand what skills are mandatory at each stage.</p>
                </div>

                <div className="include-card">
                  <div className="include-icon purple">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Learning Plan Integration</h3>
                  <p>Direct connection to AI Career Toolkit recommendations.</p>
                </div>

                <div className="include-card">
                  <div className="include-icon orange">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Timeline-Based Guidance</h3>
                  <p>Know what to focus on short-term and long-term.</p>
                </div>

                <div className="include-card">
                  <div className="include-icon teal">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Preparation Strategy</h3>
                  <p>Understand certifications, tools, and competencies needed.</p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Results Section */}
        {results && (
          <div id="career-map-results" className="results-container">
            {/* Results Header */}
            <div className="results-header">
              <div>
                <h2 className="results-title">Your Career Roadmap</h2>
                <p className="results-subtitle">{results.summary}</p>
              </div>
            </div>

            {/* Career Progression Path */}
            {results.careerProgressionPath && results.careerProgressionPath.length > 0 && (
              <div className="results-section">
                <h3 className="section-subtitle">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" stroke="url(#progressGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs>
                      <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#10b981"/>
                        <stop offset="100%" stopColor="#059669"/>
                      </linearGradient>
                    </defs>
                  </svg>
                  Career Progression Path
                </h3>
                <div className="flowchart">
                  {results.careerProgressionPath.map((step, index) => (
                    <div 
                      key={index}
                      className={`flowchart-node ${step.isCurrent ? 'node-current' : ''} ${step.isDreamRole ? 'node-dream' : ''} ${index === results.careerProgressionPath.length - 1 ? 'node-last' : ''} ${index === 0 ? 'node-first' : ''}`}
                    >
                      <div className="flowchart-rail">
                        <div className="rail-line rail-line-top"></div>
                        <div className="flowchart-node-dot">
                          {step.isCurrent ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          ) : step.isDreamRole ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#fff" stroke="#fff" strokeWidth="1"/></svg>
                          ) : (
                            <span className="dot-number">{step.step}</span>
                          )}
                        </div>
                        <div className="rail-line rail-line-bottom"></div>
                      </div>
                      <div className="flowchart-arm"></div>
                      <div className="flowchart-node-content">
                        <div className="flowchart-node-header">
                          <div className="flowchart-node-badges">
                            {step.isCurrent && <span className="step-badge current-badge">Current</span>}
                            {step.isDreamRole && <span className="step-badge dream-badge">Dream Role</span>}
                            <span className="step-num">Step {step.step}</span>
                          </div>
                          <div className="progression-duration">{step.duration}</div>
                        </div>
                        <h4 className="progression-role">{step.roleTitle}</h4>
                        <p className="progression-description">{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skill Requirements */}
            {results.skillRequirements && (
              <div className="results-section">
                <h3 className="section-subtitle">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" stroke="url(#skillsGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs>
                      <linearGradient id="skillsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#3b82f6"/>
                        <stop offset="100%" stopColor="#2563eb"/>
                      </linearGradient>
                    </defs>
                  </svg>
                  Skill Requirements Breakdown
                </h3>
                <div className="skill-levels">
                  {['currentLevel', 'intermediateLevel', 'advancedLevel'].map((level) => {
                    const levelData = results.skillRequirements[level];
                    if (!levelData) return null;
                    
                    const levelTitles = {
                      currentLevel: 'Current Level',
                      intermediateLevel: 'Intermediate Level',
                      advancedLevel: 'Advanced Level'
                    };

                    return (
                      <div key={level} className="skill-level-card">
                        <h4 className="skill-level-title">{levelTitles[level]}</h4>
                        
                        {levelData.technical && levelData.technical.length > 0 && (
                          <div className="skill-category">
                            <h5>Technical Skills</h5>
                            <div className="skill-tags">
                              {levelData.technical.map((skill, idx) => (
                                <span key={idx} className="skill-tag technical">{skill}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {levelData.soft && levelData.soft.length > 0 && (
                          <div className="skill-category">
                            <h5>Soft Skills</h5>
                            <div className="skill-tags">
                              {levelData.soft.map((skill, idx) => (
                                <span key={idx} className="skill-tag soft">{skill}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {levelData.tools && levelData.tools.length > 0 && (
                          <div className="skill-category">
                            <h5>Tools & Technologies</h5>
                            <div className="skill-tags">
                              {levelData.tools.map((tool, idx) => (
                                <span key={idx} className="skill-tag tools">{tool}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Learning Plan */}
            {results.learningPlan && (
              <div className="results-section">
                <h3 className="section-subtitle">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" stroke="url(#learningGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs>
                      <linearGradient id="learningGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#8b5cf6"/>
                        <stop offset="100%" stopColor="#7c3aed"/>
                      </linearGradient>
                    </defs>
                  </svg>
                  Learning Plan
                </h3>
                <div className="flowchart">
                  {['immediateActions', 'shortTerm', 'midTerm', 'longTerm'].map((phase, index, arr) => {
                    const phaseData = results.learningPlan[phase];
                    if (!phaseData) return null;

                    const phaseInfo = {
                      immediateActions: { title: 'Immediate Actions', color: '#10b981', icon: '⚡' },
                      shortTerm: { title: 'Short Term', color: '#3b82f6', icon: '📅' },
                      midTerm: { title: 'Mid Term', color: '#8b5cf6', icon: '🎯' },
                      longTerm: { title: 'Long Term', color: '#f59e0b', icon: '🚀' }
                    };

                    return (
                      <div 
                        key={phase} 
                        className={`flowchart-node ${index === arr.length - 1 ? 'node-last' : ''} ${index === 0 ? 'node-first' : ''}`}
                      >
                        <div className="flowchart-rail">
                          <div className="rail-line rail-line-top"></div>
                          <div className="flowchart-node-dot" style={{ background: phaseInfo[phase].color, boxShadow: `0 0 16px ${phaseInfo[phase].color}55` }}>
                            <span style={{ fontSize: '16px' }}>{phaseInfo[phase].icon}</span>
                          </div>
                          <div className="rail-line rail-line-bottom"></div>
                        </div>
                        <div className="flowchart-arm"></div>
                        <div className="flowchart-node-content learning-node-content">
                          <div className="phase-header" style={{ borderLeftColor: phaseInfo[phase].color }}>
                            <h4>{phaseInfo[phase].title}</h4>
                            <span className="phase-timeline">{phaseData.timeline}</span>
                          </div>
                          
                          {phaseData.focus && phaseData.focus.length > 0 && (
                            <div className="phase-focus">
                              <strong>Focus Areas:</strong>
                              <ul>
                                {phaseData.focus.map((item, idx) => (
                                  <li key={idx}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {phaseData.courses && phaseData.courses.length > 0 && (
                            <div className="phase-courses">
                              <strong>Recommended Courses:</strong>
                              <div className="courses-grid">
                                {phaseData.courses.map((course, idx) => (
                                  <div key={idx} className="course-card-mini">
                                    <div className="course-name">{course.name}</div>
                                    <div className="course-provider">{course.provider}</div>
                                    {course.duration && <div className="course-duration">{course.duration}</div>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {phaseData.outcome && (
                            <div className="phase-outcome">
                              <strong>Expected Outcome:</strong> {phaseData.outcome}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Preparation Strategy */}
            {results.preparationStrategy && (
              <div className="results-section">
                <h3 className="section-subtitle">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="url(#strategyGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs>
                      <linearGradient id="strategyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#14b8a6"/>
                        <stop offset="100%" stopColor="#0d9488"/>
                      </linearGradient>
                    </defs>
                  </svg>
                  Preparation Strategy
                </h3>
                
                <div className="strategy-grid">
                  {/* Certifications */}
                  {results.preparationStrategy.certifications && results.preparationStrategy.certifications.length > 0 && (
                    <div className="strategy-card">
                      <h4 className="strategy-heading">Certifications</h4>
                      <div className="certifications-list">
                        {results.preparationStrategy.certifications.map((cert, idx) => (
                          <div key={idx} className="certification-item">
                            <div className="cert-header">
                              <span className="cert-name">{cert.name}</span>
                              <span className={`cert-priority ${cert.priority}`}>{cert.priority}</span>
                            </div>
                            <div className="cert-details">
                              <span className="cert-provider">{cert.provider}</span>
                              <span className="cert-time">{cert.timeToComplete}</span>
                            </div>
                            {cert.stage && <div className="cert-stage">Stage: {cert.stage}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Projects */}
                  {results.preparationStrategy.projects && results.preparationStrategy.projects.length > 0 && (
                    <div className="strategy-card">
                      <h4 className="strategy-heading">Portfolio Projects</h4>
                      <div className="projects-list">
                        {results.preparationStrategy.projects.map((project, idx) => (
                          <div key={idx} className="project-item">
                            <h5 className="project-title">{project.title}</h5>
                            <p className="project-description">{project.description}</p>
                            {project.skills && project.skills.length > 0 && (
                              <div className="project-skills">
                                {project.skills.map((skill, sIdx) => (
                                  <span key={sIdx} className="project-skill-tag">{skill}</span>
                                ))}
                              </div>
                            )}
                            {project.stage && <div className="project-stage">Stage: {project.stage}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Networking */}
                  {results.preparationStrategy.networking && results.preparationStrategy.networking.length > 0 && (
                    <div className="strategy-card">
                      <h4 className="strategy-heading">Networking Activities</h4>
                      <div className="networking-list">
                        {results.preparationStrategy.networking.map((activity, idx) => (
                          <div key={idx} className="networking-item">
                            <div className="networking-activity">{activity.activity}</div>
                            <div className="networking-why">{activity.why}</div>
                            <div className="networking-when">Timeline: {activity.when}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tools to Master */}
                  {results.preparationStrategy.toolsToMaster && results.preparationStrategy.toolsToMaster.length > 0 && (
                    <div className="strategy-card">
                      <h4 className="strategy-heading">Tools to Master</h4>
                      <div className="tools-list">
                        {results.preparationStrategy.toolsToMaster.map((tool, idx) => (
                          <div key={idx} className="tool-item">
                            <div className="tool-header">
                              <span className="tool-name">{tool.tool}</span>
                              <span className={`tool-priority ${tool.priority}`}>{tool.priority}</span>
                            </div>
                            <div className="tool-category">{tool.category}</div>
                            {tool.learningPath && <div className="tool-learning">{tool.learningPath}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Timeline Guidance */}
            {results.timelineGuidance && (
              <div className="results-section">
                <h3 className="section-subtitle">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="url(#timelineGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs>
                      <linearGradient id="timelineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f59e0b"/>
                        <stop offset="100%" stopColor="#d97706"/>
                      </linearGradient>
                    </defs>
                  </svg>
                  Timeline Guidance
                </h3>

                {results.timelineGuidance.totalEstimatedTime && (
                  <div className="total-time">
                    <strong>Total Estimated Time:</strong> {results.timelineGuidance.totalEstimatedTime}
                  </div>
                )}

                {results.timelineGuidance.milestones && results.timelineGuidance.milestones.length > 0 && (
                  <div className="milestones-section">
                    <h4>Key Milestones</h4>
                    <div className="milestones-timeline">
                      {results.timelineGuidance.milestones.map((milestone, idx) => (
                        <div key={idx} className="milestone-item">
                          <div className="milestone-timeline">{milestone.timeline}</div>
                          <div className="milestone-achievement">{milestone.achievement}</div>
                          <div className="milestone-validation">{milestone.validation}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {results.timelineGuidance.criticalDecisionPoints && results.timelineGuidance.criticalDecisionPoints.length > 0 && (
                  <div className="decision-points-section">
                    <h4>Critical Decision Points</h4>
                    <div className="decision-points">
                      {results.timelineGuidance.criticalDecisionPoints.map((point, idx) => (
                        <div key={idx} className="decision-point">
                          <div className="decision-when">{point.when}</div>
                          <div className="decision-question">{point.decision}</div>
                          {point.options && point.options.length > 0 && (
                            <ul className="decision-options">
                              {point.options.map((option, oIdx) => (
                                <li key={oIdx}>{option}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Industry Insights */}
            {results.industryInsights && (
              <div className="results-section">
                <h3 className="section-subtitle">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" stroke="url(#industryGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <defs>
                      <linearGradient id="industryGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#ec4899"/>
                        <stop offset="100%" stopColor="#db2777"/>
                      </linearGradient>
                    </defs>
                  </svg>
                  Industry Insights
                </h3>

                <div className="insights-grid">
                  {results.industryInsights.marketDemand && (
                    <div className="insight-card">
                      <h4>Market Demand</h4>
                      <p>{results.industryInsights.marketDemand}</p>
                    </div>
                  )}

                  {results.industryInsights.salaryProgression && (
                    <div className="insight-card salary-card">
                      <h4>Salary Progression</h4>
                      <div className="salary-ranges">
                        <div className="salary-range">
                          <span className="salary-label">Current Role</span>
                          <span className="salary-value">{results.industryInsights.salaryProgression.currentRange}</span>
                        </div>
                        <div className="salary-range">
                          <span className="salary-label">Intermediate</span>
                          <span className="salary-value">{results.industryInsights.salaryProgression.intermediateRange}</span>
                        </div>
                        <div className="salary-range">
                          <span className="salary-label">Target Role</span>
                          <span className="salary-value highlight">{results.industryInsights.salaryProgression.targetRange}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {results.industryInsights.growthOpportunities && results.industryInsights.growthOpportunities.length > 0 && (
                    <div className="insight-card">
                      <h4>Growth Opportunities</h4>
                      <ul>
                        {results.industryInsights.growthOpportunities.map((opportunity, idx) => (
                          <li key={idx}>{opportunity}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {results.industryInsights.challenges && results.industryInsights.challenges.length > 0 && (
                    <div className="insight-card">
                      <h4>Challenges to Consider</h4>
                      <ul>
                        {results.industryInsights.challenges.map((challenge, idx) => (
                          <li key={idx}>{challenge}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {results.industryInsights.successFactors && results.industryInsights.successFactors.length > 0 && (
                    <div className="insight-card">
                      <h4>Success Factors</h4>
                      <ul>
                        {results.industryInsights.successFactors.map((factor, idx) => (
                          <li key={idx}>{factor}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Next Steps Section */}
            <div className="results-section">
              <h3 className="section-subtitle">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" stroke="url(#nextStepsGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <defs>
                    <linearGradient id="nextStepsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3b82f6"/>
                      <stop offset="100%" stopColor="#2563eb"/>
                    </linearGradient>
                  </defs>
                </svg>
                Next Steps
              </h3>
              <div className="cm-next-steps-grid">
                <div className="cm-next-step-card">
                  <h4>📚 Continue Learning</h4>
                  <p>Use the AI Career Toolkit to get personalized course recommendations based on your skill gaps and career goals.</p>
                  <button className="cm-next-step-btn cm-toolkit-btn" onClick={() => navigate('/ai-improvement/ai-career-toolkit')}>
                    Go to AI Career Toolkit →
                  </button>
                </div>
                <div className="cm-next-step-card">
                  <h4>📝 Test Your Knowledge</h4>
                  <p>Take an MCQ test to assess your current skills and identify areas where you need improvement.</p>
                  <button className="cm-next-step-btn cm-mcq-btn" onClick={() => navigate('/ai-improvement/take-mock-interview')}>
                    Take MCQ Test →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AICareerMap;
